import { TableRow } from "./TableRow";
import { ToolsTableCell } from "./ToolsTableCell";
import { Alignment, DOMUtil, LogManager, UserLayoutConfigManager } from "@mcleod/core";
import { TableColumn } from "./TableColumn";
import { Table } from "./Table";
import { HeadingTableCell } from "./HeadingTableCell";
import { ResizeManager, ResizeStorageProps } from "../../base/ResizeManager";
import { HeadingTableCellResizerStyles } from "./HeadingTableCellResizerStyles";
import { ComponentListenerDefs } from "../../base/ComponentListenerDefs";
import { ResizeEvent } from "../../events/ResizeEvent";

interface Sibling {
    cell: HeadingTableCell;
    index: number;
    currentWidth: number;
}

const log = LogManager.getLogger("components.table.HeadingTableCellResizer");
const DEFAULT_MIN_WIDTH = 50;

export class HeadingTableCellResizer extends ResizeManager<HeadingTableCell> {
    public static readonly LOCAL_SETTINGS_KEY = "tableColumn.settings";

    constructor(cell: HeadingTableCell) {
        super({ component: cell, defaultMinWidth: DEFAULT_MIN_WIDTH, dragEdge: Alignment.RIGHT });
    }

    protected override createResizeDiv(): HTMLDivElement {
        const div = document.createElement('div');
        div.id = `resizer-${this.cell.id}`;
        div.classList.add(HeadingTableCellResizerStyles.resizer);
        return div;
    }

    protected override get shouldSyncResizer(): boolean {
        return this.table
            && this.table.isDeserializing() !== true
            && this.table.columnsResizable === true
            && this.cell._designer == null
            && this.cell.isFixedWidth !== true
    }

    public override sync() {
        super.sync();
        if (this.canResize) {
            this.preventTotalCollapse();
        }
    }

    protected override onResizeMove(e: MouseEvent, newWidth: number) {
        if (this.table.isHorizontallyScrollable)
            this.columnOnlyResize(newWidth);
        else
            this.siblingResize(newWidth, e);
    }

    private columnOnlyResize = (newWidth: number): void => {
        log.debug(`Performing column resize for scrollable table.`);
        this.setColumnWidth(this.cell, this.index, newWidth);
    }

    private siblingResize = (newWidth: number, e?: MouseEvent): void => {
        // Calculate actual change in width
        const widthChange = newWidth - this.componentWidth;
        if (widthChange === 0)
            return; // No change, nothing to do

        log.debug(`Performing column resize for non-scrollable table.`);
        const shrinkSiblings: boolean = widthChange > 0;
        const siblingCells: Sibling[] = this.headingCells
            .map ((cell: HeadingTableCell, index: number): Sibling => {
                return { cell: cell, index: index, currentWidth: this.actualWidth(cell) };
            })
            .filter((sibling: Sibling) => {
                return sibling.index !== this.index && sibling.cell.visible && !sibling.cell.isFixedWidth
            })
            .sort((a: Sibling, b: Sibling) => {
                return shrinkSiblings ? (b.currentWidth - a.currentWidth) : (a.currentWidth - b.currentWidth)
            });

        let changeRemaining = Math.abs(widthChange);
        for (const sibling of siblingCells) {
            if (changeRemaining <= 0) break;

            const widthAdjustment = shrinkSiblings ? (-1 * changeRemaining) : changeRemaining;
            const newWidth = this.newSiblingWidth(sibling, widthAdjustment, shrinkSiblings, e);
            this.setColumnWidth(sibling.cell, sibling.index, newWidth);
            changeRemaining -= Math.abs(sibling.currentWidth - newWidth);
        }

        this.setColumnWidth(this.cell, this.index, newWidth);
    }

    private setColumnWidth(cell: HeadingTableCell, columnIndex: number, newWidth: string | number): void {
        cell.width = newWidth;
        this.updateColumnCellWidths(columnIndex, newWidth);
    }

    protected override onResizeEnd(_e: MouseEvent) {
        this.table.recalculateVirtualPlaceholders();
        this.table.fireListeners(ComponentListenerDefs.resize, new ResizeEvent(this.table));
    }

    private updateColumnCellWidths(columnIndex: number, width: string | number): void {
        // Update all cells in the column to match the new width
        for (const row of this.table.allRows)
            this.updateColumnForRow(row, columnIndex, width);

        if (this.table.quickAddRow)
            this.updateColumnForRow(this.table.quickAddRow, columnIndex, width);
    }

    private updateColumnForRow(row: TableRow, columnIndex: number, newColumnWidth: string | number) {
        if (row.cells.length > columnIndex) {
            const cell = row.cells[columnIndex];
            if (cell && !(cell instanceof ToolsTableCell)) {
                cell.width = newColumnWidth;
            }
        }
    }

    private newSiblingWidth(sibling: Sibling, widthAdjustment: number, isShrinking: boolean, e?: MouseEvent): number {
        // if holding the ctrl or meta key, don't resize siblings. Which, in effect, will the table scrollable, if manipulated column is increasing width.
        if (e && (e.ctrlKey || e.metaKey))
            return sibling.currentWidth;

        const potentialSiblingWidth = sibling.currentWidth + widthAdjustment;
        if (isShrinking)
            return Math.max(potentialSiblingWidth, this.actualMinWidth(sibling.cell))

        const maximumWidth = this.actualMaxWidth(sibling.cell);
        return maximumWidth ? Math.min(potentialSiblingWidth, maximumWidth) : potentialSiblingWidth;
    }

    private preventTotalCollapse() {
        if (!this.component.minWidth)
            this.component.minWidth = this.componentMinWidth + "px";
    }

    protected override get storageProps(): ResizeStorageProps {
        return {
            localSettingsKey: HeadingTableCellResizer.LOCAL_SETTINGS_KEY,
            currentUserSettings: this.currentColumnWidths,
            applyUserSettings: this.applySavedColumnWidth,
            componentSettingsKey: () => this.table.getCustomColumnWidthsSettingsKey(),
            ignoreSaveScreenSizeSetting: true
        }
    }

    private currentColumnWidths(): any {
        const columnSettings: Record<string, string | number> = {};
        this.headingCells.forEach((heading: HeadingTableCell) => {
            if (heading.id && heading.visible)
                columnSettings[heading.id] = this.getColumnWidthToStore(heading);
        });
        return columnSettings;
    }

    /**
     * Determines the column width to store. If the cell is fixed width or a part of a horizontally
     * scrolling table, we save the actual width of the cell. Otherwise, we save the value as a percentage
     * of the overall width. We do this as an attempt to keep cell sizes proportional because there is no
     * guarantee that the browser window is the same width as it was when the table's cell sizes were last saved.
     * @param cell
     */
    private getColumnWidthToStore(cell: HeadingTableCell): string | number {
        if (cell.isFixedWidth || this.table.isHorizontallyScrollable) {
            return this.actualWidth(cell);
        }
        const tableWidth = DOMUtil.getElementWidth(
            this.table.tableHeadingRow._element, { includePadding: false }
        );
        return `${DOMUtil.getElementWidthPercentage(cell._element, tableWidth)}%`;
    }

    private applySavedColumnWidth(settings: any) {
        const width = settings?.[this.cell.id]
        if (width)
            this.setColumnWidth(this.cell, this.index, width);
    }

    private get cell(): HeadingTableCell {
        return this.component;
    }

    private get headingCells(): HeadingTableCell[] {
        return this.cell.table.columns.map(
            (column: TableColumn): HeadingTableCell => column.headingCell
        );
    }

    private get table(): Table {
        return this.cell.table;
    }

    private get index(): number {
        return this.headingCells.indexOf(this.cell);
    }

    protected override get containingElement(): HTMLElement {
        return this.table._element;
    }
}

UserLayoutConfigManager.registerForServerSync(HeadingTableCellResizer.LOCAL_SETTINGS_KEY);
